import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TableColumn,
  StandardizedTable,
  formatNumber as stdFormatNumber,
  formatCurrency as stdFormatCurrency,
  TrendIndicator,
  TableRow,
} from "@/components/ui/standardized-table";
import type { TableQueryDTO } from "@/types/table-query";
import { getTopSellingProductsApiV1TopSellingProductsGet } from "@/backend-sdk/client/sdk.gen";
import type {
  GetTopSellingProductsApiV1TopSellingProductsGetData,
  ProductsDashboardTopSellingResponse,
} from "@/backend-sdk/client/types.gen";
import { useAccount } from "@/contexts/account.context";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { ValorIndisponivel } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { useDrilldownOptional } from "@/features/drilldown";

/** Os produtos saem do e-commerce do GA4. */
const FONTES_DA_TABELA: readonly RequisitoDeFonte[] = ["ga4"];

const renderND = (erro: boolean) => (
  <ValorIndisponivel fontes={FONTES_DA_TABELA} erro={erro} />
);

type TopSellingProductsResponse = ProductsDashboardTopSellingResponse;
type TopSellingProductItem = TopSellingProductsResponse["items"][number];
/**
 * A linha da tabela: o item que o servidor publica, com as quatro métricas e os dois campos
 * de sessão normalizados para número, mais a única variação que não é publicada achatada.
 */
type TopSellingProductRow = TopSellingProductItem & {
  items_viewed: number;
  items_added_to_cart: number;
  items_purchased: number;
  purchase_revenue: number;
  sessions: number;
  session_conversion_rate: number;
  session_conversion_rate_change: number | null;
};

type TopSellingProductsCrossFilterKey = Extract<
  keyof GetTopSellingProductsApiV1TopSellingProductsGetData["query"],
  `filter_${string}`
>;
type TopSellingProductsCrossFilters = Pick<
  GetTopSellingProductsApiV1TopSellingProductsGetData["query"],
  TopSellingProductsCrossFilterKey
>;

const getTopSellingProductsCrossFilters = (
  filters: Record<string, string> | undefined,
): Partial<TopSellingProductsCrossFilters> => ({
  filter_search_term: filters?.filter_search_term,
  filter_device: filters?.filter_device,
  filter_region: filters?.filter_region,
  filter_city: filters?.filter_city,
  filter_category: filters?.filter_category,
  // <M-212> `filter_category2` a `filter_category5`, `filter_brand` e `filter_variant` saíram:
  // `DrillDimension` não tem mais as seis dimensões, então `apiFilters` não tem como trazer
  // as chaves. As rotas continuam declarando os parâmetros — o que sumiu é quem os produzia.
  filter_product: filters?.filter_product,
  filter_date: filters?.filter_date,
});

const ProdutosMaisVendidosTable: React.FC<{
  initialData?: TopSellingProductsResponse;
  compact?: boolean;
  excludeColumns?: string[];
  /**
   * <M-212> Se a página em que a tabela está desenhada tem como honrar o recorte que o
   * clique produz — ver o mesmo campo em `CategoriasMaisVendidasTable`, inclusive quanto ao
   * padrão `false`.
   *
   * ⚠️ `/top-selling-products`, que é a fonte desta tabela, não aplica nenhum dos treze
   * `filter_*` que declara: mesmo ligada, ela nunca se recorta a si mesma.
   */
  drilldownEnabled?: boolean;
}> = ({
  initialData,
  compact = false,
  excludeColumns,
  drilldownEnabled = false,
}) => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const contextoDeDrill = useDrilldownOptional();
  // <M-212> O contexto continua sendo lido para os filtros que **outras** tabelas produzem;
  // o que `drilldownEnabled` desliga é a emissão, isto é, o clique nesta tabela.
  const drilldown = drilldownEnabled ? contextoDeDrill : null;
  const [rows, setRows] = useState<TopSellingProductRow[]>([]);
  const [totalRow, setTotalRow] = useState<
    TopSellingProductsResponse["total_row"] | undefined
  >(initialData?.total_row || undefined);
  // Começa carregando: a busca só sai no efeito, e a tabela vazia antes dela parecia "sem dados".
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const erro = error !== null;
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const PAGE_SIZE = 10;
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  // Handler para clique na linha (cross-filtering com suporte a Ctrl+click multi-select)
  const handleRowClick = useCallback(
    (row: TableRow, index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;

      const productName = row.item_name as string;
      if (!productName || productName === "N/D") return;

      const isMultiSelect = event?.ctrlKey || event?.metaKey;
      drilldown.selectRow("product", productName, productName, isMultiSelect);
    },
    [drilldown],
  );

  // Verifica se uma linha está selecionada
  const isRowSelected = useCallback(
    (row: TableRow, index: number): boolean => {
      if (!drilldown) return false;
      const productName = row.item_name as string;
      return drilldown.isSelected("product", productName);
    },
    [drilldown],
  );

  const renderDeltaCell = (value: unknown): React.ReactNode => {
    const change = typeof value === "number" ? value : null;
    if (change === null || Number.isNaN(change)) {
      return <span className="text-muted-foreground">—</span>;
    }
    return <TrendIndicator value={change} />;
  };

  // Todas as colunas disponíveis (valor e % Δ em colunas separadas, padrão do sistema)
  const allColumns: TableColumn[] = useMemo(
    () => [
      {
        key: "item_name",
        header: "Nome do Item",
        align: "left",
        alwaysVisible: true,
        render: (value, item, index, isTotal) => {
          const label =
            !value || String(value).toUpperCase() === "N/D"
              ? renderND(erro)
              : value;
          return isTotal ? (
            value
          ) : (
            <div className="flex items-center gap-1">
              <span>{`${index + 1}. `}</span>
              {label}
            </div>
          );
        },
      },
      {
        key: "items_viewed",
        header: "Itens Vistos",
        align: "right",
        type: "number",
        render: (value) =>
          !value || value === 0 ? renderND(erro) : stdFormatNumber(value),
      },
      {
        key: "items_viewed_change_percentage",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell,
      },
      {
        key: "sessions",
        header: "Sessões",
        align: "right",
        type: "number",
        visible: false,
        render: (value) => {
          const numeric =
            typeof value === "number" ? value : Number(value || 0);
          const hasValue = Number.isFinite(numeric) && numeric > 0;
          return !hasValue ? renderND(erro) : stdFormatNumber(numeric);
        },
      },
      {
        key: "sessions_change_percentage",
        header: "% Δ",
        align: "right" as const,
        visible: false,
        render: renderDeltaCell,
      },
      {
        key: "session_conversion_rate",
        header: "Tx Conv. da Sessão",
        align: "right",
        type: "percent",
        visible: false,
        render: (value) => {
          const numeric =
            typeof value === "number" ? value : Number(value || 0);
          const hasValue = Number.isFinite(numeric) && numeric > 0;
          return !hasValue ? renderND(erro) : `${numeric.toFixed(2)}%`;
        },
      },
      {
        key: "session_conversion_rate_change",
        header: "% Δ",
        align: "right" as const,
        visible: false,
        // A única das oito variações que a resposta não publica achatada — ela só existe
        // dentro de `changes`, e em `queryMode="server"` o `sort_by` emitido é a chave da
        // coluna.
        sortable: false,
        render: renderDeltaCell,
      },
      {
        key: "items_added_to_cart",
        header: "Carrinhos",
        align: "right",
        type: "number",
        render: (value) =>
          !value || value === 0 ? renderND(erro) : stdFormatNumber(value),
      },
      {
        key: "items_added_to_cart_change_percentage",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell,
      },
      {
        key: "cart_rate",
        header: "Tx Carrinho",
        align: "right",
        type: "percent",
        // <M-202> `/top-selling-products` publica esta taxa desde sempre, e a célula a
        // recalculava por cima — `items_added_to_cart / items_viewed`, a mesma fórmula. Foi
        // recalcular uma taxa assim que manteve a de carrinho escondida por uma migração
        // inteira no card irmão, que não a recebia; ver D-048.
        //
        // ⚠️ O campo é opcional no SDK, e uma resposta sem ele desenha "N/D", não
        // "0,00 %": esta tela pode receber uma resposta em cache de uma versão que não o
        // trazia, e um zero ao lado de colunas com visualizações e carrinhos seria um número
        // errado sem aviso.
        //
        // ⚠️ A última casa decimal muda em empates exatos: o `toFixed(2)` arredondava meio
        // para cima sobre a divisão feita aqui, e o servidor arredonda meio para o par.
        render: (value, item) => {
          const vistos = item?.items_viewed || 0;
          if (vistos === 0 || typeof value !== "number") return renderND(erro);
          return `${value.toFixed(2)}%`;
        },
      },
      {
        key: "cart_rate_change_percentage",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell,
      },
      {
        key: "items_purchased",
        header: "Itens Comprados",
        align: "right",
        type: "number",
        render: (value) =>
          !value || value === 0 ? renderND(erro) : stdFormatNumber(value),
      },
      {
        key: "items_purchased_change_percentage",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell,
      },
      {
        key: "conversion_rate",
        header: "Tx Conv. Itens",
        align: "right",
        type: "percent",
        // <M-202> Idem: a conversão por carrinho vem pronta, e a célula a repetia. O "N/D"
        // continua vindo do denominador — sem carrinho não há conversão a mostrar, e ali o
        // servidor publica `0`.
        render: (value, item) => {
          const carrinhos = item?.items_added_to_cart || 0;
          if (carrinhos === 0 || typeof value !== "number")
            return renderND(erro);
          return `${value.toFixed(2)}%`;
        },
      },
      {
        key: "conversion_rate_change_percentage",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell,
      },
      {
        key: "purchase_revenue",
        header: "Receita do Item",
        align: "right",
        type: "currency",
        render: (value) =>
          !value || value === 0 ? renderND(erro) : stdFormatCurrency(value),
      },
      {
        key: "purchase_revenue_change_percentage",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell,
      },
    ],
    [erro],
  );

  // Modo compacto: apenas Nome, Itens Comprados e Receita do Item (com deltas)
  const columns: TableColumn[] = useMemo(() => {
    const compactKeys = new Set([
      "item_name",
      "items_purchased",
      "items_purchased_change_percentage",
      "purchase_revenue",
      "purchase_revenue_change_percentage",
    ]);
    let cols = compact
      ? allColumns.filter((c) => compactKeys.has(c.key))
      : allColumns;
    if (excludeColumns?.length) {
      // A coluna "% Δ" de uma métrica pode chamar-se `<k>_change` (quando a variação só vive
      // em `changes`) ou `<k>_change_percentage` (quando o servidor a publica achatada).
      const excludeSet = new Set(
        excludeColumns.flatMap((k) => [
          k,
          `${k}_change`,
          `${k}_change_percentage`,
        ]),
      );
      cols = cols.filter((c) => !excludeSet.has(c.key));
    }
    return cols;
  }, [allColumns, compact, excludeColumns]);

  const fetchData = useCallback(
    async (pg: number) => {
      if (!globalFilter) return;
      try {
        setLoading(true);
        setError(null);

        const startDate = formatDateToISO(globalFilter.dateRange.startDate);
        const endDate = formatDateToISO(globalFilter.dateRange.endDate);
        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;
        // Para produtos, não usar account_id - dados estão agregados por customer
        const accountId = undefined;

        const crossFilters = getTopSellingProductsCrossFilters(
          contextoDeDrill?.apiFilters,
        );
        // A tabela não se filtra por si mesma: a lista de produtos tem de continuar inteira
        // para que a linha selecionada tenha de onde ser desmarcada.
        delete crossFilters.filter_product;

        const { data: resp } =
          await getTopSellingProductsApiV1TopSellingProductsGet({
            query: {
              dimension: "product",
              ...crossFilters,
              start_date: startDate,
              end_date: endDate,
              comparison: globalFilter.comparison?.type || "previous_period",
              customer_uuid: customerUuid,
              account_id: accountId,
              page: pg,
              page_size: PAGE_SIZE,
              q: tableQuery?.q,
              sort_by: tableQuery?.sortBy,
              sort_dir: tableQuery?.sortDir,
              filters: tableQuery?.filters?.length
                ? JSON.stringify(tableQuery.filters)
                : undefined,
            },
            throwOnError: true,
          });

        // As colunas usam a chave que o servidor publica, sem renomear: em
        // `queryMode="server"` o `sort_by` que a tabela emite é a chave da coluna, e um nome
        // que só existe aqui pediria à rota uma ordenação por um campo que a resposta dela
        // não tem. A única exceção é `session_conversion_rate_change`, que só vive dentro de
        // `changes` — e a coluna dela é declarada não ordenável.
        const mapped = (resp.items || []).map((it: TopSellingProductItem) => {
          const ch = it.changes || {};
          return {
            ...it,
            items_viewed: it.items_viewed || 0,
            items_added_to_cart: it.items_added_to_cart || 0,
            items_purchased: it.items_purchased || 0,
            purchase_revenue: it.purchase_revenue || 0,
            sessions:
              typeof it.sessions === "number"
                ? it.sessions
                : Number(it.sessions || 0),
            session_conversion_rate:
              typeof it.session_conversion_rate === "number"
                ? it.session_conversion_rate
                : Number(it.session_conversion_rate || 0),
            session_conversion_rate_change: ch.session_conversion_rate ?? null,
          };
        });
        setRows(mapped);
        setTotalRow(resp.total_row || undefined);
        setPage(resp.pagination?.page || pg);
        setTotal(resp.pagination?.total || 0);
      } catch (err) {
        console.error("❌ Erro ao buscar Produtos Mais Vendidos:", err);
        setError(err instanceof Error ? err.message : "Erro ao carregar dados");
        setRows([]);
        setTotalRow(undefined);
      } finally {
        setLoading(false);
      }
    },
    [globalFilter, selectedAccount, tableQuery, contextoDeDrill],
  );

  // Resetar para a primeira página quando filtros globais ou cross-filters mudarem
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1);
  }, [globalFilter, contextoDeDrill?.apiFiltersKey]);

  // Sempre que filtros globais / paginação / query da tabela mudarem, refetch no server
  useEffect(() => {
    if (!globalFilter) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchData(page);
  }, [globalFilter, page, fetchData, contextoDeDrill?.apiFiltersKey]);

  return (
    <StandardizedTable
      title="PRODUTOS MAIS VENDIDOS"
      description={
        drilldown
          ? "Clique em uma linha para filtrar a página inteira"
          : undefined
      }
      columns={columns}
      // Numa nova busca, as linhas anteriores ficam sob o véu de carregamento da tabela.
      data={error ? [] : rows}
      loading={loading}
      // <M-201> O rodapé desenha as seis variações. Elas ficavam escondidas porque o
      // servidor publicava quatro de verdade, a da taxa de carrinho nula e a de conversão
      // fixa em `0`; desde M-201 as seis são a variação das somas, e desde M-278 o período
      // anterior inclui o produto que parou de vender.
      //
      // ⚠️ Sem linha nenhuma, o servidor manda o item vazio — nome em branco, zeros e a
      // conversão em `0` —, e ele não é rodapé de nada: a tabela vazia fica sem "Total Geral".
      total={totalRow && total > 0 ? totalRow : undefined}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: total,
        onPageChange: (p) => setPage(p),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setPage(1);
      }}
      onRowClick={drilldown ? handleRowClick : undefined}
      selectedRowIndex={drilldown ? isRowSelected : undefined}
      clickableRows={!!drilldown}
    />
  );
};

export default ProdutosMaisVendidosTable;
